import {
  beSmsMessageApiGetSmsMsgDetails,
  beSmsMessageApiSearchSmsMessages,
} from './../../../api/beSmsMessageApi'
import {
  beGeolocationApiGetCountries,
  beGeolocationApiGetRegionsByCountryCode,
} from 'Api/beGeolocationApi'
import { STORE_KEY_ID_LIST, storeGet } from 'mailup-react-helpers'
import {
  beRecipientsGetAllGroups,
  beRecipientsGetDynamicFields,
} from '../../../api/beRecipientsApi'
import { type PagedList } from '../../../types/beFiltersApi'
import { type DynamicField, type Group } from '../../../types/beRecipientsApi'
import { beListApiGetTags } from '../../../api/beListApi'
import { type Tag } from '../../../types/beListApi'
import { type LabelValuePair } from '../../../types/common'
import { MAX_SELECT_ITEMS } from '../../../config/config'
import {
  beEmailMessageApiSearchEmailMessages,
  beEmailMessageApiGetMessageLinks,
  beEmailMessageApiGetEmailMsgLazy,
} from '../../../api/beEmailMessageApi'
import getIntl from '../../../helpers/intl'
import { activityDefaultMsgOpts } from '../utils/conditionOptions'
import { type GeoCountry, type GeoRegion } from '../types'

export const getAllGroups = async (): Promise<PagedList<Group>> => {
  return await beRecipientsGetAllGroups(storeGet(STORE_KEY_ID_LIST) as number)
}

export const getDynamicFields = async (): Promise<DynamicField[]> => {
  return await beRecipientsGetDynamicFields()
}

export const getAllTags = async (): Promise<PagedList<Tag>> => {
  const params = {
    idList: storeGet(STORE_KEY_ID_LIST) as number,
    onlyEnabled: false,
    onlyUsed: false,
    pageSize: 0,
    pageNumber: 1,
    orderBy: 'Id',
    orderDescending: true,
  }
  return await beListApiGetTags(params)
}

export const fetchEmailMessagesByName = async (
  name = '',
): Promise<{ items: Array<LabelValuePair<number>>; totalItems: number }> => {
  const emailMsgs = await beEmailMessageApiSearchEmailMessages(
    storeGet(STORE_KEY_ID_LIST) as number,
    {
      orderBy: 'Id',
      orderDescending: true,
      pageNumber: 1,
      pageSize: MAX_SELECT_ITEMS,
    },
    { SearchText: name, FullText: false },
  )
  const intl = getIntl()
  const mappedMsgs =
    emailMsgs?.Items?.map((c) => ({
      value: c.Id,
      label: `${c.Id} - ${c.Subject}`,
    })) ?? []
  const completeMessageList =
    name !== ''
      ? mappedMsgs
      : [
          ...(activityDefaultMsgOpts(intl) as Array<LabelValuePair<number>>),
        ].concat(mappedMsgs)

  return {
    items: completeMessageList,
    totalItems: emailMsgs.TotalItems,
  }
}
export const fetchEmailMsgById = async (
  idMessage: number,
): Promise<LabelValuePair<number>> => {
  const intl = getIntl()
  const defaultMsgWithProvidedId = activityDefaultMsgOpts(intl).find(
    (o) => o.value === idMessage,
  )
  if (defaultMsgWithProvidedId !== undefined) {
    return defaultMsgWithProvidedId as LabelValuePair<number>
  }
  const c = await beEmailMessageApiGetEmailMsgLazy(idMessage)
  return {
    value: c.Id,
    label: `${c.Id} - ${c.Subject}`,
  }
}

export const fetchSmsMessagesByName = async (
  name = '',
): Promise<{ items: Array<LabelValuePair<number>>; totalItems: number }> => {
  const smsMsgs = await beSmsMessageApiSearchSmsMessages(
    storeGet(STORE_KEY_ID_LIST) as number,
    {
      orderBy: 'Id',
      orderDescending: true,
      pageNumber: 1,
      pageSize: MAX_SELECT_ITEMS,
    },
    { Subject: name },
  )

  const intl = getIntl()
  const mappedMsgs =
    smsMsgs?.Items?.map((c) => ({
      value: c.Id,
      label: `${c.Id} - ${c.Subject}`,
    })) ?? []
  const completeMessageList =
    name !== ''
      ? mappedMsgs
      : [
          ...(activityDefaultMsgOpts(intl) as Array<LabelValuePair<number>>),
        ].concat(mappedMsgs)

  return {
    items: completeMessageList,
    totalItems: smsMsgs.TotalItems,
  }
}
export const fetchSmsMsgById = async (
  idMessage: number,
): Promise<LabelValuePair<number>> => {
  const intl = getIntl()
  const defaultMsgWithProvidedId = activityDefaultMsgOpts(intl).find(
    (o) => o.value === idMessage,
  )
  if (defaultMsgWithProvidedId !== undefined) {
    return defaultMsgWithProvidedId as LabelValuePair<number>
  }
  const c = await beSmsMessageApiGetSmsMsgDetails(idMessage)
  return {
    value: c.Id,
    label: `${c.Id} - ${c.Subject}`,
  }
}

export const getMessageLinksUrls = async (
  idMessage: number,
): Promise<string[]> => {
  const links = await beEmailMessageApiGetMessageLinks(idMessage)
  return links.Items.filter((c) => c.KindOfHtmlTag === 'link').map((c) => c.Url)
}

export const getGeoCountries = async (): Promise<GeoCountry[]> => {
  const { Countries } = await beGeolocationApiGetCountries()

  return Countries.map((c) => ({
    value: c.CountryCode,
    label: c.CountryName,
  }))
}

export const getGeoRegions = async (
  countryCode: string,
): Promise<GeoRegion[]> => {
  const { Regions } = await beGeolocationApiGetRegionsByCountryCode(countryCode)

  return Regions.map((r) => ({
    value: r.RegionCode,
    label: r.RegionName,
  }))
}
